// src/pages/ContactInformation.js
import React, {
  useEffect,
  useRef,
  useState,
} from "react";

import {
  useTranslation,
} from "react-i18next";

import {
  getStoredToken,
} from "../lib/authStorage";

import api from "../axiosConfig";

import {
  useCompanySettingsData,
} from "../hooks/useCompanySettingsData";

import "../styles/ContactInformation.css";

const EMPTY_CONTACT_FORM = {
  mainName: "",
  title: "",
  phone: "",
  email: "",
};

const firstDefined = (
  ...values
) => {
  for (
    const value of values
  ) {
    if (
      value !== undefined &&
      value !== null &&
      String(value).trim() !== ""
    ) {
      return String(
        value
      );
    }
  }

  return "";
};

const contactFromDetails = (
  details
) => {
  if (!details) {
    return {
      ...EMPTY_CONTACT_FORM,
    };
  }

  return {
    mainName:
      firstDefined(
        details.mainContactName,
        details.main_contact_name,
        details.contact_name,
        details.primary_contact_name,
        details.ceo,
        details.owner_name
      ),

    title:
      firstDefined(
        details.mainContactTitle,
        details.main_contact_title,
        details.contact_title,
        details.primary_contact_title,
        details.ceo
          ? "CEO"
          : ""
      ),

    phone:
      firstDefined(
        details.mainContactPhoneNumber,
        details.main_contact_phone_number,
        details.main_contact_phone,
        details.contact_phone_main
      ),

    email:
      firstDefined(
        details.companyEmail,
        details.company_email,
        details.email_company,
        details.email
      ).toLowerCase(),
  };
};

export default function ContactInformation({
  embedded = false,
  onSaved,
}) {
  const { t } =
    useTranslation(
      "contact_information"
    );

  const {
    data,
    refresh,
    patchSection,
  } =
    useCompanySettingsData();

  const details =
    data?.details ||
    null;

  const dirtyRef =
    useRef(false);

  const [
    form,
    setForm,
  ] = useState(
    () =>
      contactFromDetails(
        details
      )
  );

  const [
    detailsId,
    setDetailsId,
  ] = useState(
    details?.id ??
      null
  );

  const [
    saving,
    setSaving,
  ] = useState(false);

  useEffect(() => {
    if (
      !details ||
      dirtyRef.current
    ) {
      return;
    }

    setDetailsId(
      details.id ??
        null
    );

    setForm(
      contactFromDetails(
        details
      )
    );
  }, [
    details,
  ]);

  const setField = (
    key,
    value
  ) => {
    dirtyRef.current =
      true;

    setForm(
      (
        previous
      ) => ({
        ...previous,

        [key]:
          value,
      })
    );
  };

  const handleSubmit =
    async (
      event
    ) => {
      event.preventDefault();

      if (
        !detailsId
      ) {
        alert(
          t(
            "errors.no_record"
          )
        );

        return;
      }

      const PHONE_SOFT =
        /^[+0-9 ()-]{6,25}$/;

      if (
        form.phone &&
        !PHONE_SOFT.test(
          form.phone.trim()
        )
      ) {
        alert(
          t(
            "errors.phone_invalid"
          )
        );

        return;
      }

      setSaving(
        true
      );

      try {
        const payload = {
          mainContactName:
            form.mainName
              ?.trim() ||
            "",

          mainContactTitle:
            form.title
              ?.trim() ||
            "",

          mainContactPhoneNumber:
            form.phone
              ?.trim() ||
            "",

          companyEmail:
            form.email
              ?.trim()
              .toLowerCase() ||
            "",
        };

        await api.put(
          `/company-details/${detailsId}`,
          payload,
          {
            withCredentials:
              true,

            headers: {
              Authorization:
                `Bearer ${getStoredToken()}`,
            },
          }
        );

        dirtyRef.current =
          false;

        patchSection(
          "details",
          payload
        );

        setForm({
          mainName:
            payload.mainContactName,

          title:
            payload.mainContactTitle,

          phone:
            payload.mainContactPhoneNumber,

          email:
            payload.companyEmail,
        });

        if (
          typeof onSaved ===
          "function"
        ) {
          await onSaved();
        } else {
          await refresh();
        }

        alert(
          t(
            "toasts.saved_success"
          )
        );
      } catch (error) {
        const backendMsg =
          error?.response
            ?.data
            ?.error ||
          error?.response
            ?.data
            ?.message ||
          error?.message ||
          "";

        console.error(
          "[ContactInformation] save error:",
          error
        );

        alert(
          `${t(
            "toasts.saved_error"
          )}${
            backendMsg
              ? ` (${backendMsg})`
              : ""
          }`
        );
      } finally {
        setSaving(
          false
        );
      }
    };

  return (
    <div
      className={`contact-information ${
        embedded
          ? "contact-information--embedded"
          : ""
      }`}
    >
      <div className="contact-information__header">
        <h1 className="contact-information__title">
          {t(
            "title"
          )}
        </h1>

        <p className="contact-information__subtitle">
          {t(
            "subtitle"
          )}
        </p>
      </div>

      <form
        className="contact-information__form"
        onSubmit={
          handleSubmit
        }
        noValidate
      >
        <div className="contact-information__grid">
          <div className="contact-information__field">
            <label className="contact-information__label">
              {t(
                "fields.mainName.label"
              )}
            </label>

            <input
              className="contact-information__input"
              required
              value={
                form.mainName
              }
              onChange={(
                event
              ) =>
                setField(
                  "mainName",
                  event.target.value
                )
              }
              placeholder={t(
                "fields.mainName.placeholder"
              )}
            />
          </div>

          <div className="contact-information__field">
            <label className="contact-information__label">
              {t(
                "fields.title.label"
              )}
            </label>

            <input
              className="contact-information__input"
              required
              value={
                form.title
              }
              onChange={(
                event
              ) =>
                setField(
                  "title",
                  event.target.value
                )
              }
              placeholder={t(
                "fields.title.placeholder"
              )}
            />
          </div>

          <div className="contact-information__field">
            <label className="contact-information__label">
              {t(
                "fields.phone.label"
              )}
            </label>

            <input
              className="contact-information__input"
              type="tel"
              value={
                form.phone
              }
              onChange={(
                event
              ) =>
                setField(
                  "phone",
                  event.target.value
                )
              }
              placeholder={t(
                "fields.phone.placeholder"
              )}
              autoComplete="tel"
            />
          </div>

          <div className="contact-information__field">
            <label className="contact-information__label">
              {t(
                "fields.email.label"
              )}
            </label>

            <input
              className="contact-information__input"
              type="email"
              required
              value={
                form.email
              }
              onChange={(
                event
              ) =>
                setField(
                  "email",
                  event.target.value
                )
              }
              placeholder={t(
                "fields.email.placeholder"
              )}
              autoComplete="email"
            />
          </div>
        </div>

        <div className="contact-information__actions">
          <button
            type="button"
            className="contact-information__btn contact-information__btn--ghost"
            onClick={() =>
              window.history.back()
            }
            disabled={
              saving
            }
          >
            {t(
              "actions.cancel"
            )}
          </button>

          <button
            type="submit"
            className="contact-information__btn contact-information__btn--primary"
            disabled={
              saving
            }
          >
            {saving
              ? t(
                  "actions.saving"
                )
              : t(
                  "actions.save"
                )}
          </button>
        </div>
      </form>
    </div>
  );
}